/**
 * Phase P — Per-section Footprint Movement Report (Salem-untouched).
 *
 * Section-aware clone of `pages/Sections/Dispatch/FootprintMovementReport.jsx`.
 * Reads from `/api/per-section/{slug}/footprint-movement-report` and
 * exports CSV via the matching endpoint. Salem's source file is paired
 * with this clone in `SALEM_CLONE_DRIFT.md`.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Download, Filter, X, ArrowUpCircle, ArrowDownCircle, Users, BarChart3, RefreshCcw, Warehouse, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';

const SLUG_TO_DISPLAY = {
  color_combo: 'Color Combo',
  art_silk: 'Art Silk',
  kids: 'Kids',
};

const PerSectionFootprintMovementReport = () => {
  const navigate = useNavigate();
  const { section: slug } = useParams();
  const sectionName = SLUG_TO_DISPLAY[slug];
  const basePath = `/sections/dispatch/${slug}`;
  const apiBase = `/per-section/${slug}/footprint-movement-report`;

  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState([]);
  const [aggregations, setAggregations] = useState({});
  const [pagination, setPagination] = useState({ skip: 0, limit: 500, total: 0, showing: 0 });

  const [filters, setFilters] = useState({
    storage_type: '',
    operation_type: '',
    barcode: '',
    staff_name: '',
    bin_location: '',
    date_from: '',
    date_to: '',
  });
  const [showFilters, setShowFilters] = useState(true);

  const fetchReport = useCallback(async () => {
    if (!sectionName) return;
    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => {
        if (v && v !== 'all') params.append(k, v);
      });
      params.append('skip', pagination.skip.toString());
      params.append('limit', pagination.limit.toString());

      const response = await apiClient.get(`${apiBase}?${params}`);
      setData(response.data.data || []);
      setAggregations(response.data.aggregations || {});
      setPagination((prev) => ({
        ...prev,
        total: response.data.pagination?.total || 0,
        showing: response.data.pagination?.showing || 0,
      }));
    } catch (error) {
      toast.error('Failed to fetch report');
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  }, [apiBase, filters, pagination.skip, pagination.limit, sectionName]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const handleClearFilters = () => {
    setFilters({
      storage_type: '', operation_type: '', barcode: '',
      staff_name: '', bin_location: '', date_from: '', date_to: '',
    });
    setPagination((prev) => ({ ...prev, skip: 0 }));
  };

  const handleExportCSV = async () => {
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => {
        if (v && v !== 'all') params.append(k, v);
      });
      const response = await apiClient.get(`${apiBase}/export-csv?${params}`, {
        responseType: 'blob',
      });
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `footprint_movement_${slug}_${new Date().toISOString().slice(0, 10)}.csv`;
      link.click();
      window.URL.revokeObjectURL(url);
      toast.success('Report exported successfully');
    } catch (error) {
      toast.error('Failed to export report');
    }
  };

  const formatDateTime = (isoString) => {
    if (!isoString) return '-';
    try {
      const date = new Date(isoString);
      return date.toLocaleString('en-IN', {
        day: '2-digit', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
      });
    } catch {
      return isoString;
    }
  };

  if (!sectionName) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 p-6">
        <AlertTriangle className="w-10 h-10 text-amber-500 mb-3" />
        <h2 className="text-lg font-bold">Unknown section</h2>
        <p className="text-sm text-gray-500 mt-1">No catalog entry for slug <code className="font-mono">{slug}</code>.</p>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen bg-gray-50 p-4 md:p-6"
      data-testid={`per-section-footprint-report-${slug}`}
    >
      <div className="max-w-7xl mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate(basePath)} data-testid="back-btn">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Footprint Movement Report</h1>
              <p className="text-sm text-gray-500">{sectionName} Section · put-away & picking movements in FG/SFG storage</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={fetchReport} disabled={isLoading} data-testid="refresh-btn">
              <RefreshCcw className={`w-4 h-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button onClick={handleExportCSV} className="bg-green-600 hover:bg-green-700" data-testid="export-csv-btn">
              <Download className="w-4 h-4 mr-2" />
              Export CSV
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
          <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white" data-testid="tile-total">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs opacity-80">Total Operations</p>
                  <p className="text-2xl font-bold">{aggregations.total_records || 0}</p>
                </div>
                <BarChart3 className="w-8 h-8 opacity-50" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white" data-testid="tile-put-away">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs opacity-80">Put Away Ops</p>
                  <p className="text-2xl font-bold">{aggregations.total_put_away_ops || 0}</p>
                  <p className="text-xs opacity-70">{aggregations.total_qty_put_away || 0} qty</p>
                </div>
                <ArrowDownCircle className="w-8 h-8 opacity-50" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-orange-500 to-orange-600 text-white" data-testid="tile-picking">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs opacity-80">Picking Ops</p>
                  <p className="text-2xl font-bold">{aggregations.total_picking_ops || 0}</p>
                  <p className="text-xs opacity-70">{aggregations.total_qty_picked || 0} qty</p>
                </div>
                <ArrowUpCircle className="w-8 h-8 opacity-50" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white" data-testid="tile-fg-sfg">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs opacity-80">FG / SFG</p>
                  <p className="text-2xl font-bold">{aggregations.fg_movements || 0} / {aggregations.sfg_movements || 0}</p>
                </div>
                <Warehouse className="w-8 h-8 opacity-50" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-teal-500 to-teal-600 text-white" data-testid="tile-staff">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs opacity-80">Unique Staff</p>
                  <p className="text-2xl font-bold">{aggregations.unique_staff || 0}</p>
                  <p className="text-xs opacity-70">{aggregations.unique_barcodes || 0} SKUs</p>
                </div>
                <Users className="w-8 h-8 opacity-50" />
              </div>
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <div>
              <CardTitle className="text-lg">Movement Records</CardTitle>
              <CardDescription>
                Showing {pagination.showing || data.length} of {pagination.total} records
              </CardDescription>
            </div>
            <Button variant="ghost" size="sm" onClick={() => setShowFilters((s) => !s)} data-testid="toggle-filters-btn">
              <Filter className="w-4 h-4 mr-2" />
              {showFilters ? 'Hide Filters' : 'Show Filters'}
            </Button>
          </CardHeader>

          {showFilters && (
            <CardContent className="pt-0 pb-4 border-b">
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
                <div>
                  <label className="text-xs font-medium text-gray-700 mb-1 block">Storage Type</label>
                  <Select value={filters.storage_type} onValueChange={(val) => setFilters((p) => ({ ...p, storage_type: val }))}>
                    <SelectTrigger data-testid="filter-storage-type"><SelectValue placeholder="All" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All</SelectItem>
                      <SelectItem value="FG">FG Storage</SelectItem>
                      <SelectItem value="SFG">SFG Storage</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-700 mb-1 block">Operation</label>
                  <Select value={filters.operation_type} onValueChange={(val) => setFilters((p) => ({ ...p, operation_type: val }))}>
                    <SelectTrigger data-testid="filter-operation-type"><SelectValue placeholder="All" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All</SelectItem>
                      <SelectItem value="put_away">Put Away</SelectItem>
                      <SelectItem value="picking">Picking</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-700 mb-1 block">Barcode</label>
                  <Input value={filters.barcode} onChange={(e) => setFilters((p) => ({ ...p, barcode: e.target.value }))} placeholder="Search..." data-testid="filter-barcode" />
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-700 mb-1 block">Staff Name</label>
                  <Input value={filters.staff_name} onChange={(e) => setFilters((p) => ({ ...p, staff_name: e.target.value }))} placeholder="Search..." data-testid="filter-staff-name" />
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-700 mb-1 block">Bin Location</label>
                  <Input value={filters.bin_location} onChange={(e) => setFilters((p) => ({ ...p, bin_location: e.target.value }))} placeholder="Search..." data-testid="filter-bin-location" />
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-700 mb-1 block">Date From</label>
                  <Input type="date" value={filters.date_from} onChange={(e) => setFilters((p) => ({ ...p, date_from: e.target.value }))} data-testid="filter-date-from" />
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-700 mb-1 block">Date To</label>
                  <Input type="date" value={filters.date_to} onChange={(e) => setFilters((p) => ({ ...p, date_to: e.target.value }))} data-testid="filter-date-to" />
                </div>
              </div>
              <div className="mt-3 flex justify-end">
                <Button variant="ghost" size="sm" onClick={handleClearFilters} data-testid="clear-filters-btn">
                  <X className="w-4 h-4 mr-2" />
                  Clear Filters
                </Button>
              </div>
            </CardContent>
          )}

          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50">
                    <TableHead>Timestamp</TableHead>
                    <TableHead>Storage</TableHead>
                    <TableHead>Barcode</TableHead>
                    <TableHead>Operation</TableHead>
                    <TableHead className="text-right">Qty</TableHead>
                    <TableHead>Staff Name</TableHead>
                    <TableHead>Bin Location</TableHead>
                    <TableHead>Reference</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody data-testid="report-tbody">
                  {isLoading ? (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-10">
                        <div className="flex flex-col items-center gap-2">
                          <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
                          <span className="text-gray-500">Loading...</span>
                        </div>
                      </TableCell>
                    </TableRow>
                  ) : data.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-10 text-gray-500">
                        No movement records found for {sectionName}
                      </TableCell>
                    </TableRow>
                  ) : (
                    data.map((row, index) => (
                      <TableRow key={index} className="hover:bg-gray-50" data-testid={`report-row-${index}`}>
                        <TableCell className="text-sm">{formatDateTime(row.timestamp)}</TableCell>
                        <TableCell>
                          <Badge variant={row.storage_type === 'FG' ? 'default' : 'secondary'}>{row.storage_type}</Badge>
                        </TableCell>
                        <TableCell className="font-mono text-sm">{row.barcode}</TableCell>
                        <TableCell>
                          <Badge
                            variant="outline"
                            className={row.operation_type === 'Put Away'
                              ? 'text-green-700 border-green-300 bg-green-50'
                              : 'text-orange-700 border-orange-300 bg-orange-50'
                            }
                          >
                            {row.operation_type === 'Put Away' && <ArrowDownCircle className="w-3 h-3 mr-1" />}
                            {row.operation_type === 'Picking' && <ArrowUpCircle className="w-3 h-3 mr-1" />}
                            {row.operation_type}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right font-semibold">{row.qty}</TableCell>
                        <TableCell>{row.staff_name || '-'}</TableCell>
                        <TableCell className="font-mono text-sm">{row.bin_location || '-'}</TableCell>
                        <TableCell className="text-sm text-gray-600">{row.slip_no || '-'}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>

            {pagination.total > pagination.limit && (
              <div className="flex items-center justify-between p-4 border-t">
                <span className="text-sm text-gray-500">
                  Page {Math.floor(pagination.skip / pagination.limit) + 1} of {Math.ceil(pagination.total / pagination.limit)}
                </span>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" disabled={pagination.skip === 0} onClick={() => setPagination((p) => ({ ...p, skip: Math.max(0, p.skip - p.limit) }))} data-testid="prev-page-btn">
                    Previous
                  </Button>
                  <Button variant="outline" size="sm" disabled={pagination.skip + pagination.limit >= pagination.total} onClick={() => setPagination((p) => ({ ...p, skip: p.skip + p.limit }))} data-testid="next-page-btn">
                    Next
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PerSectionFootprintMovementReport;
